import React from 'react';
import { Avatar, ScoreChip, StagePill, BatchPill, ResultStatus } from '../../components/ui';
import { fmtDuration, resultDateText, inviteLinkOf, hasReport } from './derive';
import { batchNameOf } from './batches';

/**
 * Per-result actions (prototype: Copy link · Report / Remind; T0 item B: Reset).
 * `act` = { copy(row), report(result, examType), recording(result, person, examSet), remind(row), reset(row), resumeEmail(result) }.
 */
export function ResultActions({ r, examType, act, busyId, person, examSet }) {
  const link = inviteLinkOf(r.row);
  const completed = hasReport(r.session);
  const pending = String(r.row.invite_status || 'pending').toLowerCase() === 'pending';
  const synced = String(r.row.id).startsWith('synced-');
  return (
    <span className="acts">
      <button type="button" className="wz-linkbtn" disabled={!link} title={link ? undefined : 'No exam link yet'} onClick={() => act.copy(r.row)}>Copy link</button>
      {completed && <button type="button" className="wz-linkbtn" onClick={() => act.report(r, examType)}>Report</button>}
      {r.session?.has_recording && act.recording && (
        <button type="button" className="wz-linkbtn" title="Recording with proctoring flags" onClick={() => act.recording(r, person, examSet)}>Recording</button>
      )}
      {r.interrupted && act.resumeEmail && (
        <button type="button" className="wz-linkbtn" disabled={busyId === r.row.id} title="Email the candidate the link to continue"
          onClick={() => act.resumeEmail(r)}>
          {busyId === r.row.id ? '…' : 'Send continue link'}
        </button>
      )}
      {r.state === 'inprogress' && <button type="button" className="wz-linkbtn" onClick={() => act.reset(r.row)}>Reset</button>}
      {!completed && r.state !== 'inprogress' && !synced && (
        <button type="button" className="wz-linkbtn" disabled={busyId === r.row.id} onClick={() => act.remind(r.row)}>
          {busyId === r.row.id ? '…' : pending ? 'Send invite' : 'Remind'}
        </button>
      )}
    </span>
  );
}

/** Score cell for result rows: chip when there is a result value, "—" otherwise. */
function ScoreCell({ r }) {
  if (r.state === 'done') return <ScoreChip result={r} style={{ justifySelf: 'start' }} />;
  return <span style={{ color: 'var(--wz-ghost)' }}>—</span>;
}

/** Prototype resultRow(): one person's result in the Interview / Batch views. */
export default function ResultRow({ p, r, examType, withBatch, batchesById, act, busyId }) {
  const batches = withBatch;
  return (
    <div className={`wz-rrow ${batches ? 'wb' : 'nb'}`}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <Avatar name={p.name} size={30} />
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</span>
          <span className="wz-sub wz-ellipsis" style={{ fontSize: 12 }}>{p.email}</span>
        </span>
      </span>
      {batches && <span><BatchPill name={batchNameOf(batchesById, r.batchId)} /></span>}
      <ResultStatus state={r.state} interrupted={r.interrupted} />
      <ScoreCell r={r} />
      <span style={{ color: 'var(--wz-ink-2)' }}>{resultDateText(r)}</span>
      <span style={{ color: 'var(--wz-ink-2)' }}>{fmtDuration(r.durationSeconds)}</span>
      <span><StagePill stage={p.stage} /></span>
      <ResultActions r={r} examType={examType} act={act} busyId={busyId} person={p} />
    </div>
  );
}

/** Score-descending order used by both views (unscored last). */
export const byScore = (examSetId) => (a, b) => {
  const va = a.results[examSetId]?.state === 'done' ? (a.results[examSetId].value ?? -0.5) : -1;
  const vb = b.results[examSetId]?.state === 'done' ? (b.results[examSetId].value ?? -0.5) : -1;
  return vb - va || a.name.localeCompare(b.name);
};
